import {
    useState,
    useEffect,
    useCallback,
    ChangeEventHandler,
    KeyboardEventHandler,
} from 'react';
import { useStore } from '@ui/hooks/useStore';
import useKeyDown from '@ui/hooks/useKeyDown';
import { checkPassword } from '@utils/dataCheck';
import { ICloseEvent } from '@core/interfaces/ICloseEvent';
import { IState } from './IState';

const useAddSubuser = (onClose: ICloseEvent['onClose']) => {
    const { subusers, lang } = useStore();
    const key = useKeyDown();

    const initState: IState = {
        login: '',
        loginError: false,
        password: '',
        passwordError: false,
        passwordConfirm: '',
        passwordConfirmError: false,
    };
    const [state, setState] = useState(initState);

    const loginValidate = (login: string) => {
        if (!login) {
            setState((prev) => ({
                ...prev,
                loginError: true,
            }));
            return false;
        } else {
            setState((prev) => ({
                ...prev,
                loginError: false,
            }));
            return true;
        }
    };
    const passwordValidate = (password: string) => {
        const isPasswordValid = checkPassword(password);
        if (!isPasswordValid) {
            setState((prev) => ({
                ...prev,
                passwordError: true,
            }));
            return false;
        } else {
            setState((prev) => ({
                ...prev,
                passwordError: false,
            }));
            return true;
        }
    };
    const passwordConfirmValidate = (password: string, confirm: string) => {
        if (password !== confirm) {
            setState((prev) => ({
                ...prev,
                passwordConfirmError: true,
            }));
            return false;
        } else {
            setState((prev) => ({
                ...prev,
                passwordConfirmError: false,
            }));
            return true;
        }
    };
    const handleLoginChange: ChangeEventHandler<HTMLInputElement> = useCallback(
        (e) => {
            const login = e.target.value;
            setState((prev) => ({
                ...prev,
                login,
                loginError: false,
                passwordError: false,
                passwordConfirmError: false,
            }));
        },
        [],
    );
    const handlePasswordChange: ChangeEventHandler<HTMLInputElement> =
        useCallback((e) => {
            const password = e.target.value;
            setState((prev) => ({
                ...prev,
                password,
                loginError: false,
                passwordError: false,
                passwordConfirmError: false,
            }));
        }, []);
    const handlePasswordConfirmChange: ChangeEventHandler<HTMLInputElement> =
        useCallback((e) => {
            const passwordConfirm = e.target.value;
            setState((prev) => ({
                ...prev,
                passwordConfirm,
                loginError: false,
                passwordError: false,
                passwordConfirmError: false,
            }));
        }, []);
    const handleCreateClick = useCallback(() => {
        const { login, password, passwordConfirm } = state;
        const isLoginValid = loginValidate(login);
        const isPasswordValid = passwordValidate(password);
        const isConfirmValid = passwordConfirmValidate(
            password,
            passwordConfirm,
        );
        subusers.resetRegisterError();
        subusers.resetServerError();
        if (isLoginValid && isPasswordValid && isConfirmValid) {
            subusers.create(login, password);
        }
    }, [state.login, state.password, state.passwordConfirm]);
    const handleInputClick = useCallback(() => {
        subusers.resetRegisterError();
        subusers.resetServerError();
        setState((prev) => ({
            ...prev,
            loginError: false,
            passwordError: false,
            passwordConfirmError: false,
        }));
    }, []);
    const handleKey = (key: string) => {
        switch (key) {
            case 'Escape':
                onClose();
                break;
            case 'Enter':
                handleCreateClick();
                break;
        }
    };
    const handleKeyDown: KeyboardEventHandler<HTMLElement> = useCallback(
        (e) => {
            const { key } = e;
            if (key === 'Enter') {
                e.stopPropagation();
                e.preventDefault();
                handleKey(key);
            }
        },
        [state.login, state.password, state.passwordConfirm],
    );
    const handleBtnKeyDown: KeyboardEventHandler<HTMLButtonElement> =
        useCallback((e) => {
            const { key } = e;
            if (key === 'Enter') {
                e.stopPropagation();
                e.preventDefault();
                const target = e.target as HTMLButtonElement;
                target.click();
            }
        }, []);
    const handlers = {
        handleLoginChange,
        handlePasswordChange,
        handlePasswordConfirmChange,
        handleCreateClick,
        handleInputClick,
        handleKeyDown,
        handleBtnKeyDown,
    };

    useEffect(() => {
        subusers.resetServerError();
        subusers.resetRegisterError();
    }, []);
    useEffect(() => {
        handleKey(key);
    }, [key]);
    useEffect(() => {
        if (subusers.operationSuccess) {
            subusers.resetOperationSuccess();
            onClose();
        }
    }, [subusers.operationSuccess]);

    return {
        subusers,
        lang,
        state,
        handlers,
    };
};

export default useAddSubuser;
